import { isNotGeoPolygon } from '@records/records-search/shared/records-search.helper';
import { EntityType } from '@shared/models/entity-enum';
import { IOption } from '@shared/models/icommons.model';
import { applyFiltersOverride } from '@records/records-search/filter-set/configs/filters-override';
import {
  getViewPortPolygon,
  leafletifyShape,
  makeGeoPolygon,
  makeLatLngBounds,
  makeShapePolygon,
} from '../../records-search/esri-map/esri-map.helpers';
import { GROUP_CONFIGURATIONS } from '../../records-search/records-search.constants';
import { MapState } from './map-state.model';
import {
  DEFAULT_ENTITY,
  DEFAULT_MAP_STATE,
  DEFAULT_QUERY_STATE,
  ENTITY,
  MAP_STATE,
  QUERY_STATE,
} from './search-state.constants';

export const loadEntityFromSession = () => {
  if (!localStorage.getItem(ENTITY)) {
    localStorage.setItem(ENTITY, DEFAULT_ENTITY);
  }
  return localStorage.getItem(ENTITY);
};

// LOCAL SERVICES
export function removeFiltersWithoutKey(filters) {
  return (filters || []).filter(filter => {
    if (filter.type === 'group') {
      return !!filter.key && (!filter.values.length || filter.values.every(value => !!value.key));
    }
    return !!filter.key;
  });
}

export const loadMapFilters = ({ searchMode, currentBounds, shape }: MapState, { filters = [] }) => {
  if (searchMode === 'asMove') {
    filters = [...filters.filter(isNotGeoPolygon), makeGeoPolygon(getViewPortPolygon(currentBounds))];
  } else if (!!shape) {
    const geoPolygon = makeShapePolygon({ shape });
    filters = [...filters.filter(isNotGeoPolygon), geoPolygon];
  }
  return { filters };
};

/**
 * Filter the state filters by specific configurations to prevent issues
 * when the current user local data is not compatible with the current code version
 * @param filters
 * @param entity
 * @returns {IOption[]}
 */
const propertyFiltersResolver = (filters: IOption[], entity: EntityType) => {
  let filteredFilters = [...filters];
  if (filters.length > 0) {
    const filtersForGroupSelection = Object.values(GROUP_CONFIGURATIONS[entity] ?? {})
      .map(filterGroup =>
        Object.keys(filterGroup)
          .map(groupKey => ({ filterKeys: filterGroup[groupKey]?.selectTogheter, groupKey }))
          .flat()
      )
      .flat();

    const filterOut = (filter: IOption) =>
      filtersForGroupSelection.find(f => f.filterKeys.includes(filter.key)) &&
      (filter.group_key == null || filtersForGroupSelection.find(f => f.groupKey === filter.group_key) == null);
    filteredFilters = filtersForGroupSelection.length > 0 ? filters.filter(f => !filterOut(f)) : filters;
  }
  return removeFiltersWithoutKey(filteredFilters);
};

const defaultFilterResolver = (filters: IOption[]) => removeFiltersWithoutKey(filters);

const ResolveFiltersByEntity = {
  [EntityType.Property]: propertyFiltersResolver,
};

export const loadQueryStateFromSession = entity => {
  // Must be the per-entity, leafletified map state (searchMode/currentBounds/shape),
  // not the raw multi-entity MAP_STATE blob - otherwise loadMapFilters() below never
  // matches 'asMove'/shape and the geoPolygon filter (stripped by removeFiltersWithoutKey
  // for lacking a `key`) never gets restored after a full page reload.
  const mapState = loadMapStateFromSession(entity)[entity];

  let localStorageState = JSON.parse(localStorage.getItem(QUERY_STATE)) || {};
  localStorageState = Object.entries(localStorageState).reduce(
    (acc, [key, searchState]: [string, any]) => ({
      ...acc,
      [key]: {
        ...searchState,
        filters: applyFiltersOverride(
          ResolveFiltersByEntity[entity]?.(searchState.filters, entity) ?? defaultFilterResolver(searchState.filters)
        ),
      },
    }),
    {}
  );

  const state = localStorageState?.[entity] ?? { ...DEFAULT_QUERY_STATE, entity };
  const initialState = mapState ? { ...state, ...loadMapFilters(mapState, state) } : state;
  return { ...localStorageState, [entity]: initialState };
};

export const loadMapStateFromSession = entity => {
  const storageState = localStorage.getItem(MAP_STATE);
  const sessionStorage = !!storageState ? leafletify(JSON.parse(storageState)) : {};

  return sessionStorage[entity] ? sessionStorage : { ...sessionStorage, [entity]: DEFAULT_MAP_STATE };
};

const objectArrayToObjectObjects = (a, b) => ({ ...a, ...b });

/**
 * @param { ...[subset]: MapState }
 * @return { ...[subset]: MapState }
 */
const leafletify = state => {
  return Object.keys(state)
    .map(subset => ({ key: subset, [subset]: state[subset] }))
    .map(subset => ({ [subset.key]: leafletifyItem(subset[subset.key]) }))
    .reduce(objectArrayToObjectObjects, {});
};

/***
 * @param state: MapState
 * To convert serialized map state into valid leaflet objects (bounds or shapes)
 */
const leafletifyItem = subset => {
  let { currentBounds, shape } = subset;
  if (currentBounds) {
    currentBounds = makeLatLngBounds(currentBounds);
  }
  if (shape) {
    shape = leafletifyShape(shape);
  }
  return { ...subset, currentBounds, shape };
};
